import { ConnectorWizardFormValues } from "../components/connectors/forms/ConnectorWizard";
import {
  ConnectorSecret,
  IConnectorRow,
} from "../components/connectors/models/Connector";
import { deleteApi, getApi, postApi, putApi } from "./helpers/apiMethods";

const path = "connectors";

export interface GetConnectorsResponse {
  connectors: IConnectorRow[];
}

type CreateConnectorResponse = { connector: IConnectorRow };

type EditConnectorResponse = { connector: IConnectorRow };

export async function getConnector(
  customerId: string,
  connectorType: string,
): Promise<IConnectorRow | undefined> {
  const response = await getApi<{ connector: IConnectorRow }>(
    `/v1/customers/${customerId}/${path}/${connectorType}`,
  );

  if (!response?.data?.connector) {
    throw new Error("Connector not found");
  }
  return response.data.connector;
}

export const getConnectors = async (
  customerId: string,
): Promise<GetConnectorsResponse | undefined> => {
  try {
    const response = await getApi<GetConnectorsResponse>(
      `/v1/customers/${customerId}/${path}`,
    );

    if (!response?.data?.connectors) {
      console.warn("No connectors found.");
      return undefined;
    }

    return { connectors: response.data.connectors };
  } catch (error) {
    console.error("Error fetching connectors:", error);
    return undefined;
  }
};

export type CreateConnectorRequest = ConnectorWizardFormValues & {
  customerId: string;
};

export const createConnector = async ({
  customerId,
  type: name,
  ...request
}: CreateConnectorRequest): Promise<CreateConnectorResponse | undefined> => {
  const response = await postApi<CreateConnectorResponse>(
    `/v1/customers/${customerId}/${path}/${name}`,
    { ...request, [name]: convertConnectorSecretsToValues(request[name]) },
  );
  return response.data;
};

export type EditConnectorRequest = ConnectorWizardFormValues & {
  customerId: string;
  connectorId: string;
};

export const editConnector = async ({
  customerId,
  connectorId,
  type: name,
  ...request
}: EditConnectorRequest): Promise<EditConnectorResponse | undefined> => {
  const actualRequest = {
    ...request,
    [name]: convertConnectorSecretsToValues(request[name]),
  };
  const response = await putApi<EditConnectorResponse>(
    `/v1/customers/${customerId}/${path}/${name}/${connectorId}`,
    actualRequest,
  );
  return response.data;
};

export interface DeleteConnectorRequest {
  customerId: string;
  connectorId: string;
  jobConfigId: string;
  type: string;
}

export const deleteConnector = async ({
  customerId,
  type,
  connectorId,
  ...request
}: DeleteConnectorRequest): Promise<void> => {
  await deleteApi(
    `/v1/customers/${customerId}/${path}/${type}/${connectorId}`,
    {
      params: request,
    },
  );
};

function convertConnectorSecretsToValues(
  connectorDetails: Record<string, unknown>,
): Record<string, unknown> {
  const convertedDetails: Record<string, unknown> = { ...connectorDetails };

  for (const key in convertedDetails) {
    const value = convertedDetails[key];
    if (typeof value === "object" && value !== null && "value" in value) {
      convertedDetails[key] = (value as ConnectorSecret).value;
    }
  }

  return convertedDetails;
}
